/* ══════════════════════════════════════════════════════════════
   GDELT Map — Interactive Global News Map with Leaflet
   Loads Leaflet dynamically, fetches GDELT events, renders
   color-coded cluster markers on a dark-themed world map
   ══════════════════════════════════════════════════════════════ */

interface GDELTEvent {
    title: string;
    url: string;
    source: string;
    domain: string;
    date: string;
    tone: number;
    lat: number;
    lng: number;
    category: string;
    image?: string;
}

type LeafletMap = {
    setView(center: [number, number], zoom: number): LeafletMap;
    addLayer(layer: unknown): LeafletMap;
    on(event: string, handler: (e: { latlng?: { lat: number; lng: number } }) => void): void;
    invalidateSize(): void;
    remove(): void;
};

type LeafletLib = {
    map(el: HTMLElement, opts: Record<string, unknown>): LeafletMap;
    tileLayer(url: string, opts: Record<string, unknown>): { addTo(map: LeafletMap): void };
    circleMarker(latlng: [number, number], opts: Record<string, unknown>): { addTo(map: LeafletMap): unknown; bindPopup(content: string): unknown };
    divIcon(opts: Record<string, unknown>): unknown;
    marker(latlng: [number, number], opts: Record<string, unknown>): { addTo(map: LeafletMap): unknown; bindPopup(content: string): unknown };
};

let leafletLoaded = false;
let mapInstance: LeafletMap | null = null;
let refreshTimer: ReturnType<typeof setInterval> | null = null;

const GDELT_THEMES: Record<string, string> = {
    all: 'All Events',
    conflict: 'Conflict',
    diplomacy: 'Diplomacy',
    economy: 'Economy',
    disaster: 'Disaster',
    health: 'Health',
    protest: 'Protests',
};

let activeTheme = 'all';

/**
 * Load Leaflet CSS + JS from CDN
 */
function loadLeaflet(): Promise<LeafletLib> {
    if (leafletLoaded && (window as unknown as Record<string, unknown>).L) {
        return Promise.resolve((window as unknown as Record<string, unknown>).L as LeafletLib);
    }

    return new Promise((resolve, reject) => {
        // CSS
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(link);

        // JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = () => {
            leafletLoaded = true;
            resolve((window as unknown as Record<string, unknown>).L as LeafletLib);
        };
        script.onerror = () => reject(new Error('Failed to load Leaflet'));
        document.head.appendChild(script);
    });
}

/**
 * Fetch GDELT events with geolocation data
 */
async function fetchGDELTEvents(theme: string): Promise<GDELTEvent[]> {
    try {
        const query = theme === 'all' ? '' : `theme:${theme.toUpperCase()}`;
        const url = `https://api.gdeltproject.org/api/v2/doc/doc?query=${encodeURIComponent(query || 'world')}&mode=ArtList&maxrecords=100&format=json&timespan=24h`;

        const response = await fetch(url);
        if (!response.ok) throw new Error(`GDELT API error: ${response.status}`);

        const data = await response.json() as {
            articles?: Array<{
                title?: string;
                url?: string;
                source?: string;
                domain?: string;
                seendate?: string;
                tone?: number;
                socialimage?: string;
            }>
        };

        if (!data.articles || !Array.isArray(data.articles)) {
            return [];
        }

        // GDELT articles don't always have geocoordinates, so we simulate
        // geolocation from article content patterns
        const events: GDELTEvent[] = data.articles
            .slice(0, 80)
            .map((article) => {
                const geo = estimateGeoFromArticle(article.title || '', article.domain || '');
                return {
                    title: article.title || 'Untitled',
                    url: article.url || '#',
                    source: article.source || article.domain || 'Unknown',
                    domain: article.domain || '',
                    date: formatGDELTDate(article.seendate || ''),
                    tone: article.tone || 0,
                    lat: geo.lat,
                    lng: geo.lng,
                    category: categorizeArticle(article.title || ''),
                    image: article.socialimage,
                };
            })
            .filter(e => e.lat !== 0 || e.lng !== 0);

        return events;
    } catch {
        console.warn('[GDELT] API fetch failed');
        return [];
    }
}

/**
 * Estimate geolocation from article title & domain
 * (production systems would use GDELT's GKG which has proper geo)
 */
function estimateGeoFromArticle(title: string, domain: string): { lat: number; lng: number } {
    const geoMap: Record<string, { lat: number; lng: number }> = {
        'washington': { lat: 38.9, lng: -77.0 },
        'new york': { lat: 40.7, lng: -74.0 },
        'london': { lat: 51.5, lng: -0.1 },
        'paris': { lat: 48.8, lng: 2.3 },
        'berlin': { lat: 52.5, lng: 13.4 },
        'moscow': { lat: 55.7, lng: 37.6 },
        'beijing': { lat: 39.9, lng: 116.4 },
        'tokyo': { lat: 35.7, lng: 139.7 },
        'delhi': { lat: 28.6, lng: 77.2 },
        'mumbai': { lat: 19.1, lng: 72.9 },
        'ukraine': { lat: 48.3, lng: 31.2 },
        'kyiv': { lat: 50.4, lng: 30.5 },
        'israel': { lat: 31.8, lng: 35.2 },
        'gaza': { lat: 31.5, lng: 34.5 },
        'iran': { lat: 35.7, lng: 51.4 },
        'syria': { lat: 34.8, lng: 38.9 },
        'taiwan': { lat: 25.0, lng: 121.5 },
        'china': { lat: 35.8, lng: 104.2 },
        'australia': { lat: -25.3, lng: 133.8 },
        'canada': { lat: 56.1, lng: -106.3 },
        'brazil': { lat: -14.2, lng: -51.9 },
        'africa': { lat: 1.6, lng: 20.9 },
        'nigeria': { lat: 9.1, lng: 8.7 },
        'south korea': { lat: 35.9, lng: 127.8 },
        'korea': { lat: 37.6, lng: 127.0 },
        'japan': { lat: 36.2, lng: 138.3 },
        'india': { lat: 20.6, lng: 78.9 },
        'pakistan': { lat: 30.4, lng: 69.3 },
        'saudi': { lat: 23.9, lng: 45.1 },
        'europe': { lat: 50.1, lng: 14.4 },
        'nato': { lat: 50.8, lng: 4.4 },
        'pentagon': { lat: 38.9, lng: -77.1 },
        'trump': { lat: 38.9, lng: -77.0 },
        'biden': { lat: 38.9, lng: -77.0 },
        'congress': { lat: 38.9, lng: -77.0 },
        'kremlin': { lat: 55.7, lng: 37.6 },
        'mexico': { lat: 23.6, lng: -102.5 },
        'argentina': { lat: -38.4, lng: -63.6 },
        'germany': { lat: 51.2, lng: 10.4 },
        'france': { lat: 46.2, lng: 2.2 },
        'italy': { lat: 41.9, lng: 12.6 },
        'spain': { lat: 40.5, lng: -3.7 },
        'turkey': { lat: 38.9, lng: 35.2 },
        'egypt': { lat: 26.8, lng: 30.8 },
        'indonesia': { lat: -0.8, lng: 113.9 },
    };

    const combined = (title + ' ' + domain).toLowerCase();
    for (const [key, coords] of Object.entries(geoMap)) {
        if (combined.includes(key)) {
            // Add jitter to prevent markers stacking
            return {
                lat: coords.lat + (Math.random() - 0.5) * 3,
                lng: coords.lng + (Math.random() - 0.5) * 3,
            };
        }
    }

    return { lat: 0, lng: 0 };
}

function categorizeArticle(title: string): string {
    const lower = title.toLowerCase();
    if (/war|military|bomb|attack|missile|troops|weapon|defense|army|navy/.test(lower)) return 'conflict';
    if (/diplomat|treaty|summit|peace|negotiat|ambassador|UN|united nations/.test(lower)) return 'diplomacy';
    if (/econom|market|stock|trade|gdp|inflation|bank|fiscal|tariff/.test(lower)) return 'economy';
    if (/earthquake|flood|hurricane|tornado|wildfire|tsunami|volcano|disaster/.test(lower)) return 'disaster';
    if (/health|covid|vaccine|disease|pandemic|who|hospital|medical/.test(lower)) return 'health';
    if (/protest|rally|demonstrat|riot|strike|uprising/.test(lower)) return 'protest';
    return 'general';
}

function formatGDELTDate(dateStr: string): string {
    if (!dateStr) return 'Unknown';
    // GDELT date format: YYYYMMDDTHHmmSS or similar
    try {
        const clean = dateStr.replace('T', ' ').slice(0, 14);
        const y = clean.slice(0, 4);
        const m = clean.slice(4, 6);
        const d = clean.slice(6, 8);
        const h = clean.slice(8, 10) || '00';
        const mi = clean.slice(10, 12) || '00';
        return `${y}-${m}-${d} ${h}:${mi} UTC`;
    } catch {
        return dateStr;
    }
}

function getToneColor(tone: number): string {
    if (tone <= -5) return '#ff2d2d';
    if (tone <= -2) return '#ff8c00';
    if (tone <= 2) return '#e5c100';
    if (tone <= 5) return '#66ccff';
    return '#00ff41';
}

function getMarkerRadius(count: number): number {
    if (count >= 50) return 28;
    if (count >= 20) return 22;
    if (count >= 10) return 18;
    if (count >= 5) return 15;
    return 12;
}

/**
 * Render the GDELT Map into a container element
 */
export function renderGDELTMap(container: HTMLElement): void {
    container.innerHTML = `
    <div class="wm-map-container" id="wm-leaflet-map">
      <div class="wm-loading">
        <div class="wm-loading__spinner"></div>
        Loading map data...
      </div>
    </div>
    <div class="wm-coords" id="wm-coords">
      <div><span class="wm-coords__value" id="wm-coords-lat">0° 0' 0"</span></div>
      <div><span class="wm-coords__value" id="wm-coords-lng">0° 0' 0"</span></div>
    </div>
  `;

    initGDELTMap();
}

async function initGDELTMap(): Promise<void> {
    try {
        const L = await loadLeaflet();
        const mapEl = document.getElementById('wm-leaflet-map');
        if (!mapEl) return;

        mapEl.innerHTML = ''; // Clear loading

        // Initialize map
        mapInstance = L.map(mapEl, {
            center: [20, 0] as [number, number],
            zoom: 2,
            minZoom: 2,
            maxZoom: 12,
            zoomControl: true,
            attributionControl: true,
            worldCopyJump: true,
        } as Record<string, unknown>);

        // Dark tile layer (CartoDB dark_all — matches world-monitor aesthetic)
        L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
            attribution: '&copy; OpenStreetMap &copy; CARTO',
            subdomains: 'abcd',
            maxZoom: 19,
        } as Record<string, unknown>).addTo(mapInstance);

        // Track mouse coordinates
        mapInstance.on('mousemove', (e) => {
            if (e.latlng) {
                updateCoords(e.latlng.lat, e.latlng.lng);
            }
        });

        // Load GDELT events and add markers
        await loadAndRenderEvents(L);

        // Auto-refresh every 5 minutes
        refreshTimer = setInterval(() => {
            if (mapInstance) {
                loadAndRenderEvents(L);
            }
        }, 300000);

    } catch (err) {
        console.error('[GDELTMap] Init failed:', err);
        const mapEl = document.getElementById('wm-leaflet-map');
        if (mapEl) {
            mapEl.innerHTML = '<div class="wm-empty">⚠ Failed to load map. Check your internet connection.</div>';
        }
    }
}

let currentMarkers: unknown[] = [];

async function loadAndRenderEvents(L: LeafletLib): Promise<void> {
    const events = await fetchGDELTEvents(activeTheme);

    // Clear old markers
    currentMarkers.forEach(m => {
        try { (m as { remove(): void }).remove(); } catch { /* noop */ }
    });
    currentMarkers = [];

    const mapEl = document.getElementById('wm-leaflet-map');
    if (events.length === 0) {
        if (mapEl && !mapEl.querySelector('.wm-empty')) {
            const empty = document.createElement('div');
            empty.className = 'wm-empty';
            empty.textContent = 'No geo-located GDELT events available right now.';
            empty.style.position = 'absolute';
            empty.style.top = '1rem';
            empty.style.left = '1rem';
            empty.style.zIndex = '500';
            empty.style.background = 'rgba(7, 14, 9, 0.78)';
            empty.style.padding = '0.5rem 0.75rem';
            empty.style.border = '1px solid rgba(0,255,65,0.18)';
            mapEl.appendChild(empty);
        }
        return;
    }

    mapEl?.querySelector('.wm-empty')?.remove();

    // Cluster nearby events
    const clusters = clusterEvents(events, 5);

    clusters.forEach(cluster => {
        const avgTone = cluster.events.reduce((s, e) => s + e.tone, 0) / cluster.events.length;
        const color = getToneColor(avgTone);
        const radius = getMarkerRadius(cluster.events.length);

        const icon = L.divIcon({
            className: '',
            html: `<div class="wm-marker-cluster" style="width:${radius * 2}px;height:${radius * 2}px;background:radial-gradient(circle, ${color}aa, ${color}66);box-shadow:0 0 ${radius}px ${color}44;">${cluster.events.length}</div>`,
            iconSize: [radius * 2, radius * 2] as unknown,
            iconAnchor: [radius, radius] as unknown,
        });

        const popupContent = cluster.events.length === 1
            ? renderSinglePopup(cluster.events[0])
            : renderClusterPopup(cluster.events);

        const marker = L.marker(
            [cluster.lat, cluster.lng] as [number, number],
            { icon } as Record<string, unknown>
        );
        marker.addTo(mapInstance!);
        marker.bindPopup(popupContent);

        currentMarkers.push(marker);
    });
}

interface EventCluster {
    lat: number;
    lng: number;
    events: GDELTEvent[];
}

function clusterEvents(events: GDELTEvent[], radiusDeg: number): EventCluster[] {
    const clusters: EventCluster[] = [];
    const used = new Set<number>();

    for (let i = 0; i < events.length; i++) {
        if (used.has(i)) continue;
        const cluster: GDELTEvent[] = [events[i]];
        used.add(i);

        for (let j = i + 1; j < events.length; j++) {
            if (used.has(j)) continue;
            const d = Math.sqrt(
                Math.pow(events[i].lat - events[j].lat, 2) +
                Math.pow(events[i].lng - events[j].lng, 2)
            );
            if (d < radiusDeg) {
                cluster.push(events[j]);
                used.add(j);
            }
        }

        const avgLat = cluster.reduce((s, e) => s + e.lat, 0) / cluster.length;
        const avgLng = cluster.reduce((s, e) => s + e.lng, 0) / cluster.length;
        clusters.push({ lat: avgLat, lng: avgLng, events: cluster });
    }

    return clusters;
}

function renderSinglePopup(event: GDELTEvent): string {
    return `
    <div class="wm-popup__title">${escapeHtml(event.title)}</div>
    <div class="wm-popup__meta">
      <span class="wm-popup__source">${escapeHtml(event.source)}</span>
      <span>${event.date}</span>
      <span>Tone: ${event.tone.toFixed(1)}</span>
    </div>
    <a class="wm-popup__link" href="${event.url}" target="_blank" rel="noopener">Read Article →</a>
  `;
}

function renderClusterPopup(events: GDELTEvent[]): string {
    const items = events.slice(0, 5).map(e => `
    <div style="padding:4px 0;border-bottom:1px solid rgba(0,255,65,0.1);">
      <div class="wm-popup__title" style="font-size:10px;margin-bottom:2px;">${escapeHtml(e.title)}</div>
      <div class="wm-popup__meta">
        <span class="wm-popup__source">${escapeHtml(e.source)}</span>
        <span>${e.date}</span>
      </div>
    </div>
  `).join('');

    const more = events.length > 5 ? `<div style="padding:4px 0;font-size:9px;color:#6b8f6b;">+${events.length - 5} more events</div>` : '';
    return `<div style="max-height:300px;overflow-y:auto;">${items}${more}</div>`;
}

function escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

function updateCoords(lat: number, lng: number): void {
    const latEl = document.getElementById('wm-coords-lat');
    const lngEl = document.getElementById('wm-coords-lng');
    if (latEl) latEl.textContent = `${lat.toFixed(4)}°${lat >= 0 ? 'N' : 'S'}`;
    if (lngEl) lngEl.textContent = `${lng.toFixed(4)}°${lng >= 0 ? 'E' : 'W'}`;
}

/**
 * Get the available theme filters for external use
 */
export function getGDELTThemes(): Record<string, string> {
    return GDELT_THEMES;
}

/**
 * Set active theme filter and reload markers
 */
export function setGDELTTheme(theme: string): void {
    activeTheme = theme;
    if (mapInstance && leafletLoaded) {
        const L = (window as unknown as Record<string, unknown>).L as LeafletLib;
        loadAndRenderEvents(L);
    }
}

/**
 * Cleanup map resources
 */
export function destroyGDELTMap(): void {
    if (refreshTimer) clearInterval(refreshTimer);
    if (mapInstance) {
        mapInstance.remove();
        mapInstance = null;
    }
    currentMarkers = [];
}
